import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  MessageSquare, Users, FileText, BarChart3, Phone, Search,
  Plus, Pencil, Trash2, Copy, ExternalLink, ChevronDown,
  AlertTriangle, CheckCircle, Clock, ArrowRightLeft, Eye,
  Send, UserCheck, UserX, RefreshCw, Upload, X, Camera
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "advisors", label: "Asesores", icon: Users },
  { id: "assignments", label: "Asignaciones", icon: UserCheck },
  { id: "templates", label: "Plantillas", icon: FileText },
  { id: "communications", label: "Comunicaciones", icon: MessageSquare },
];

const STATUS_BADGE = {
  active: { label: "Activo", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  inactive: { label: "Inactivo", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  absent: { label: "Ausente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  vacation: { label: "Vacaciones", cls: "bg-sky-100 text-sky-700 border-sky-200" },
};

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
}

function AdvisorImage({ advisor, size = "w-8 h-8" }) {
  const src = `${API}/api/advisors/${advisor.id}/avatar`;
  const photoSrc = advisor.photo_url && advisor.photo_url.includes("/photo/view")
    ? `${API}${advisor.photo_url}`
    : null;
  return (
    <img
      src={photoSrc || src}
      alt={advisor.name || ""}
      className={`${size} rounded-full object-cover border-2 border-slate-200`}
      onError={e => { e.target.src = src; }}
      data-testid={`advisor-avatar-${advisor.id}`}
    />
  );
}

export default function WhatsappDashboard() {
  const { api } = useAuth();
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="space-y-6" data-testid="whatsapp-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl font-bold text-slate-900 uppercase tracking-tight" data-testid="wa-page-title">
            Comunicacion WhatsApp
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestion de comunicaciones cliente-asesor</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="wa-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`wa-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "dashboard" && <DashboardTab api={api} />}
      {tab === "advisors" && <AdvisorsTab api={api} />}
      {tab === "assignments" && <AssignmentsTab api={api} />}
      {tab === "templates" && <TemplatesTab api={api} />}
      {tab === "communications" && <CommunicationsTab api={api} />}
    </div>
  );
}


// ═══════════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════════

function DashboardTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/wa-dashboard/summary").then(r => setData(r.data)).catch(() => toast.error("Error cargando dashboard")).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="text-center py-12 text-sm text-slate-400">Cargando...</div>;
  if (!data) return null;

  const kpis = [
    { label: "Clientes asignados", value: data.total_assignments, icon: UserCheck, color: "text-emerald-600" },
    { label: "Solicitudes pendientes", value: data.unassigned_requests, icon: AlertTriangle, color: data.unassigned_requests > 0 ? "text-red-600" : "text-slate-400" },
    { label: "Comunicaciones hoy", value: data.comms_today, icon: MessageSquare, color: "text-sky-600" },
    { label: "Comms. semana", value: data.comms_week, icon: BarChart3, color: "text-indigo-600" },
    { label: "WA abiertos hoy", value: data.wa_opens_today, icon: Send, color: "text-emerald-600" },
    { label: "Lookups publicos hoy", value: data.public_lookups_today, icon: Search, color: "text-amber-600" },
  ];

  return (
    <div className="space-y-6" data-testid="wa-dashboard-content">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((k, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`wa-kpi-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <k.icon className={`w-4 h-4 ${k.color}`} />
              <span className="text-xs text-slate-500 font-medium">{k.label}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900 font-mono">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Absent advisors alert */}
      {data.absent_advisors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="wa-absent-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Asesores ausentes ({data.absent_advisors.length})</span>
          </div>
          <div className="space-y-1">
            {data.absent_advisors.map(a => (
              <p key={a.id} className="text-xs text-amber-700">
                {a.display_name} - {STATUS_BADGE[a.status]?.label || a.status}
                {a.backup_advisor_id && <span className="text-amber-500 ml-1">(tiene suplente)</span>}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* By Advisor */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Actividad por asesor</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Asesor</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-right px-4 py-2">Clientes</th>
                <th className="text-right px-4 py-2">Comms hoy</th>
                <th className="text-left px-4 py-2">Ultima comm.</th>
              </tr>
            </thead>
            <tbody>
              {data.by_advisor.map(a => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-900">{a.name}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={`text-[10px] ${STATUS_BADGE[a.status]?.cls || "bg-slate-100 text-slate-500"}`}>
                      {STATUS_BADGE[a.status]?.label || a.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{a.assigned_clients}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{a.comms_today}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{a.last_comm_at ? formatDate(a.last_comm_at) : "Sin comms."}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent requests */}
      {data.recent_requests.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Solicitudes de atencion recientes</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {data.recent_requests.map(r => (
              <div key={r.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{r.client_name}</p>
                  <p className="text-xs text-slate-500">{r.client_phone} - {r.message_text}</p>
                </div>
                <span className="text-xs text-slate-400">{formatDate(r.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════
// ADVISORS TAB
// ═══════════════════════════════════════════

function AdvisorsTab({ api }) {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignFrom, setReassignFrom] = useState(null);
  const [reassignTo, setReassignTo] = useState("");

  const load = useCallback(() => {
    api.get("/advisors?include_inactive=true").then(r => setAdvisors(r.data)).catch(() => toast.error("Error cargando asesores")).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editData.id) {
        await api.put(`/advisors/${editData.id}`, editData);
        toast.success("Asesor actualizado");
      } else {
        await api.post("/advisors", editData);
        toast.success("Asesor creado");
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/advisors/${id}/status`, { status });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const deleteAdvisor = async (id) => {
    if (!window.confirm("Desactivar este asesor?")) return;
    try {
      await api.delete(`/advisors/${id}`);
      toast.success("Asesor desactivado");
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const doReassign = async () => {
    try {
      const r = await api.post(`/advisors/${reassignFrom.id}/reassign-clients`, { target_advisor_id: reassignTo });
      toast.success(`${r.data.reassigned} clientes reasignados a ${r.data.to}`);
      setReassignOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const openNew = () => {
    setEditData({ name: "", employee_code: "", title: "Asesor de Servicio", phone: "", whatsapp_phone: "", email: "", center: "", working_hours_text: "L-V 08:00-14:00, 15:30-18:30", status: "active", accepts_new_assignments: true, backup_advisor_id: "", notes: "" });
    setEditOpen(true);
  };

  const openEdit = (a) => {
    setEditData({ ...a });
    setEditOpen(true);
  };

  if (loading) return <div className="text-center py-12 text-sm text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-4" data-testid="wa-advisors-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Asesores de servicio ({advisors.length})</h3>
        <Button onClick={openNew} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-4" data-testid="wa-add-advisor-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Asesor
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="wa-advisors-table">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="text-left px-4 py-2">Nombre</th>
              <th className="text-left px-4 py-2">Cargo</th>
              <th className="text-left px-4 py-2">Telefono WA</th>
              <th className="text-left px-4 py-2">Centro</th>
              <th className="text-left px-4 py-2">Estado</th>
              <th className="text-right px-4 py-2">Clientes</th>
              <th className="text-right px-4 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {advisors.map(a => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <AdvisorImage advisor={a} />
                    <div>
                      <p className="font-medium text-slate-900">{a.name}</p>
                      <p className="text-[10px] text-slate-400">{a.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{a.title}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{a.whatsapp_phone || a.phone}</td>
                <td className="px-4 py-2.5">{a.center}</td>
                <td className="px-4 py-2.5">
                  <Badge className={`text-[10px] ${STATUS_BADGE[a.status]?.cls || "bg-slate-100"}`}>
                    {STATUS_BADGE[a.status]?.label || a.status}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-right font-mono">{a.assigned_clients ?? 0}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`wa-edit-advisor-${a.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    {a.status === "active" && <Button variant="ghost" size="sm" onClick={() => changeStatus(a.id, "absent")} title="Marcar ausente"><UserX className="w-3.5 h-3.5 text-amber-600" /></Button>}
                    {a.status !== "active" && a.active && <Button variant="ghost" size="sm" onClick={() => changeStatus(a.id, "active")} title="Activar"><CheckCircle className="w-3.5 h-3.5 text-emerald-600" /></Button>}
                    <Button variant="ghost" size="sm" onClick={() => { setReassignFrom(a); setReassignTo(""); setReassignOpen(true); }} title="Reasignar clientes"><ArrowRightLeft className="w-3.5 h-3.5 text-sky-600" /></Button>
                    {a.active && <Button variant="ghost" size="sm" onClick={() => deleteAdvisor(a.id)} title="Desactivar"><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit/Create Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[85vh] overflow-y-auto" data-testid="wa-advisor-modal">
          <DialogHeader>
            <DialogTitle className="heading-industrial uppercase">{editData?.id ? "Editar Asesor" : "Nuevo Asesor"}</DialogTitle>
          </DialogHeader>
          {editData && (
            <AdvisorEditForm editData={editData} setEditData={setEditData} advisors={advisors} api={api} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="wa-save-advisor-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Modal */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="wa-reassign-modal">
          <DialogHeader>
            <DialogTitle className="heading-industrial uppercase">Reasignar clientes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Transferir todos los clientes de <strong>{reassignFrom?.name}</strong> a:
          </p>
          <Select value={reassignTo} onValueChange={setReassignTo}>
            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleccionar asesor destino" /></SelectTrigger>
            <SelectContent>
              {advisors.filter(x => x.id !== reassignFrom?.id && x.active && x.status === "active").map(x => (
                <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={doReassign} disabled={!reassignTo} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="wa-confirm-reassign-btn">Reasignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════
// ADVISOR EDIT FORM WITH PHOTO UPLOAD
// ═══════════════════════════════════════════

function AdvisorEditForm({ editData, setEditData, advisors, api }) {
  const fileInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const avatarSrc = `${API}/api/advisors/${editData.id}/avatar`;
  const currentPhotoSrc = editData.photo_url && editData.photo_url.includes("/photo/view")
    ? `${API}${editData.photo_url}`
    : null;
  const displaySrc = photoPreview || currentPhotoSrc || avatarSrc;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato no soportado. Usa JPG, PNG o WebP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no puede superar 5MB");
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadPhoto = async () => {
    if (!photoFile || !editData.id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", photoFile);
      const res = await api.post(`/advisors/${editData.id}/photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditData(prev => ({ ...prev, photo_url: res.data.photo_url }));
      setPhotoFile(null);
      toast.success("Foto subida correctamente");
    } catch {
      toast.error("Error al subir la foto");
    }
    setUploading(false);
  };

  const removePhoto = async () => {
    if (!editData.id) return;
    try {
      await api.delete(`/advisors/${editData.id}/photo`);
      setEditData(prev => ({ ...prev, photo_url: "" }));
      setPhotoPreview(null);
      setPhotoFile(null);
      toast.success("Foto eliminada");
    } catch {
      toast.error("Error al eliminar foto");
    }
  };

  // Upload immediately if photo selected and advisor exists
  useEffect(() => {
    if (photoFile && editData.id) uploadPhoto();
  }, [photoFile]);

  return (
    <div className="space-y-3">
      {/* Avatar / Photo section */}
      <div className="flex flex-col items-center gap-3 pb-4 border-b border-slate-100">
        <div className="relative group">
          <div className="w-28 h-28 rounded-full overflow-hidden border-3 border-slate-200 bg-slate-100 shadow-inner">
            <img
              src={displaySrc}
              alt={editData.name || "Avatar"}
              className="w-full h-full object-cover"
              onError={e => { e.target.src = avatarSrc; }}
              data-testid="advisor-edit-avatar"
            />
          </div>
          {editData.id && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              data-testid="advisor-photo-overlay"
            >
              <Camera className="w-6 h-6 text-white" />
            </button>
          )}
        </div>
        {editData.id && (
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="advisor-photo-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-sm text-[10px] uppercase tracking-wider h-7 px-3"
              data-testid="advisor-upload-photo-btn"
            >
              <Upload className="w-3 h-3 mr-1" /> {uploading ? "Subiendo..." : "Cambiar foto"}
            </Button>
            {(currentPhotoSrc || photoPreview) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={removePhoto}
                className="rounded-sm text-[10px] uppercase tracking-wider h-7 px-3 text-red-500 hover:text-red-700"
                data-testid="advisor-remove-photo-btn"
              >
                <X className="w-3 h-3 mr-1" /> Quitar
              </Button>
            )}
          </div>
        )}
        {!editData.id && (
          <p className="text-[10px] text-slate-400">Guarda primero para poder subir una foto</p>
        )}
      </div>

      <div>
        <label className="text-xs text-slate-500 font-medium">Nombre completo *</label>
        <Input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} data-testid="wa-advisor-name" className="rounded-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 font-medium">Cargo</label>
          <Input value={editData.title} onChange={e => setEditData({ ...editData, title: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Codigo empleado</label>
          <Input value={editData.employee_code} onChange={e => setEditData({ ...editData, employee_code: e.target.value })} className="rounded-sm" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 font-medium">Telefono</label>
          <Input value={editData.phone} onChange={e => setEditData({ ...editData, phone: e.target.value })} data-testid="wa-advisor-phone" className="rounded-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Telefono WhatsApp</label>
          <Input value={editData.whatsapp_phone} onChange={e => setEditData({ ...editData, whatsapp_phone: e.target.value })} placeholder="Mismo si vacio" className="rounded-sm" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 font-medium">Email</label>
          <Input value={editData.email} onChange={e => setEditData({ ...editData, email: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Centro</label>
          <Input value={editData.center} onChange={e => setEditData({ ...editData, center: e.target.value })} className="rounded-sm" />
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-500 font-medium">Horario</label>
        <Input value={editData.working_hours_text} onChange={e => setEditData({ ...editData, working_hours_text: e.target.value })} className="rounded-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 font-medium">Estado</label>
          <Select value={editData.status} onValueChange={v => setEditData({ ...editData, status: v })}>
            <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activo</SelectItem>
              <SelectItem value="absent">Ausente</SelectItem>
              <SelectItem value="vacation">Vacaciones</SelectItem>
              <SelectItem value="inactive">Inactivo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Suplente</label>
          <Select value={editData.backup_advisor_id || "__none__"} onValueChange={v => setEditData({ ...editData, backup_advisor_id: v === "__none__" ? "" : v })}>
            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Sin suplente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sin suplente</SelectItem>
              {advisors.filter(x => x.id !== editData.id && x.active).map(x => (
                <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-500 font-medium">Notas</label>
        <textarea value={editData.notes} onChange={e => setEditData({ ...editData, notes: e.target.value })} className="w-full border border-slate-200 rounded-sm p-2 text-sm h-16 resize-none" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={editData.accepts_new_assignments} onChange={e => setEditData({ ...editData, accepts_new_assignments: e.target.checked })} />
        Acepta nuevas asignaciones
      </label>
    </div>
  );
}



// ═══════════════════════════════════════════
// ASSIGNMENTS TAB
// ═══════════════════════════════════════════

function AssignmentsTab({ api }) {
  const [items, setItems] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/client-assignments?search=${search}`),
      api.get("/advisors"),
    ]).then(([a, adv]) => { setItems(a.data); setAdvisors(adv.data); }).catch(() => toast.error("Error")).finally(() => setLoading(false));
  }, [api, search]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editData.id) {
        await api.put(`/client-assignments/${editData.id}`, editData);
        toast.success("Asignacion actualizada");
      } else {
        await api.post("/client-assignments", editData);
        toast.success("Asignacion creada");
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Desactivar esta asignacion?")) return;
    try {
      await api.delete(`/client-assignments/${id}`);
      toast.success("Asignacion desactivada");
      load();
    } catch { toast.error("Error"); }
  };

  const openNew = () => {
    setEditData({ client_name: "", client_phone: "", client_email: "", client_dni: "", advisor_id: "", vehicle_plate: "", vehicle_info: "", expediente_id: "", order_number: "", notes: "" });
    setEditOpen(true);
  };

  if (loading) return <div className="text-center py-12 text-sm text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-4" data-testid="wa-assignments-tab">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar cliente, matricula, OR..." className="pl-9 rounded-sm" data-testid="wa-assignment-search" />
          </div>
          <span className="text-xs text-slate-500">{items.length} asignaciones</span>
        </div>
        <Button onClick={openNew} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-4" data-testid="wa-add-assignment-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="wa-assignments-table">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="text-left px-4 py-2">Cliente</th>
              <th className="text-left px-4 py-2">Telefono</th>
              <th className="text-left px-4 py-2">Matricula</th>
              <th className="text-left px-4 py-2">Vehiculo</th>
              <th className="text-left px-4 py-2">Asesor</th>
              <th className="text-left px-4 py-2">OR</th>
              <th className="text-left px-4 py-2">Fecha</th>
              <th className="text-right px-4 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <p className="font-medium text-slate-900">{a.client_name}</p>
                  <p className="text-[10px] text-slate-400">{a.client_email}</p>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs">{a.client_phone}</td>
                <td className="px-4 py-2.5 font-mono text-xs font-bold">{a.vehicle_plate}</td>
                <td className="px-4 py-2.5 text-slate-600 text-xs">{a.vehicle_info}</td>
                <td className="px-4 py-2.5 text-slate-700 font-medium">{a.advisor_name}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{a.order_number}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{formatDate(a.assigned_at)}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => { setEditData({ ...a }); setEditOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deactivate(a.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400 text-sm">Sin asignaciones</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit/Create Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[85vh] overflow-y-auto" data-testid="wa-assignment-modal">
          <DialogHeader>
            <DialogTitle className="heading-industrial uppercase">{editData?.id ? "Editar Asignacion" : "Nueva Asignacion"}</DialogTitle>
          </DialogHeader>
          {editData && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Nombre cliente *</label>
                  <Input value={editData.client_name} onChange={e => setEditData({ ...editData, client_name: e.target.value })} data-testid="wa-assign-client-name" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Telefono</label>
                  <Input value={editData.client_phone} onChange={e => setEditData({ ...editData, client_phone: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Email</label>
                  <Input value={editData.client_email} onChange={e => setEditData({ ...editData, client_email: e.target.value })} className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">DNI/NIE</label>
                  <Input value={editData.client_dni} onChange={e => setEditData({ ...editData, client_dni: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-medium">Asesor asignado *</label>
                <Select value={editData.advisor_id} onValueChange={v => setEditData({ ...editData, advisor_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="wa-assign-advisor-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {advisors.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name} ({a.center})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Matricula</label>
                  <Input value={editData.vehicle_plate} onChange={e => setEditData({ ...editData, vehicle_plate: e.target.value })} className="rounded-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Vehiculo</label>
                  <Input value={editData.vehicle_info} onChange={e => setEditData({ ...editData, vehicle_info: e.target.value })} placeholder="Ej: Fiat 500 2022" className="rounded-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Num. Orden</label>
                  <Input value={editData.order_number} onChange={e => setEditData({ ...editData, order_number: e.target.value })} className="rounded-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Expediente</label>
                  <Input value={editData.expediente_id} onChange={e => setEditData({ ...editData, expediente_id: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-medium">Notas</label>
                <textarea value={editData.notes} onChange={e => setEditData({ ...editData, notes: e.target.value })} className="w-full border border-slate-200 rounded-sm p-2 text-sm h-16 resize-none" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="wa-save-assignment-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════
// TEMPLATES TAB
// ═══════════════════════════════════════════

function TemplatesTab({ api }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [previewRendered, setPreviewRendered] = useState("");

  const load = useCallback(() => {
    api.get("/wa-templates?active_only=false").then(r => setTemplates(r.data)).catch(() => toast.error("Error")).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editData.id) {
        await api.put(`/wa-templates/${editData.id}`, editData);
        toast.success("Plantilla actualizada");
      } else {
        await api.post("/wa-templates", editData);
        toast.success("Plantilla creada");
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/wa-templates/${id}/toggle`);
      toast.success("Estado cambiado");
      load();
    } catch { toast.error("Error"); }
  };

  const preview = async (tpl) => {
    setPreviewTpl(tpl);
    const vars = { cliente: "Pedro Garcia", asesor: "Javier Martin", vehiculo: "Fiat 500 2022", matricula: "1234ABC", empresa: "Ascauto", horario_asesor: "L-V 08:00-18:30", telefono_asesor: "+34600000001", numero_expediente: "EXP-001", numero_orden: "OR-001" };
    try {
      const r = await api.post("/wa-templates/render-preview", { content: tpl.content, variables: vars });
      setPreviewRendered(r.data.rendered);
    } catch {
      setPreviewRendered(tpl.content);
    }
    setPreviewOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Mensaje copiado")).catch(() => toast.error("Error al copiar"));
  };

  if (loading) return <div className="text-center py-12 text-sm text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-4" data-testid="wa-templates-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Plantillas de mensaje ({templates.length})</h3>
        <Button onClick={() => { setEditData({ name: "", category: "general", description: "", content: "", variables: [], is_favorite: false, sort_order: 0 }); setEditOpen(true); }} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-4" data-testid="wa-add-template-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map(t => (
          <div key={t.id} className={`bg-white border rounded-sm p-4 ${t.is_active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`wa-template-${t.slug}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h4 className="text-sm font-semibold text-slate-900">{t.name}</h4>
                <p className="text-[10px] text-slate-400 uppercase">{t.category}</p>
              </div>
              <div className="flex items-center gap-1">
                {t.is_favorite && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[9px]">Favorita</Badge>}
                <Badge className={`text-[9px] ${t.is_active ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}`}>
                  {t.is_active ? "Activa" : "Inactiva"}
                </Badge>
              </div>
            </div>
            {t.description && <p className="text-xs text-slate-500 mb-2">{t.description}</p>}
            <div className="bg-slate-50 border border-slate-100 rounded-sm p-3 text-xs text-slate-700 mb-3 whitespace-pre-wrap font-mono leading-relaxed">
              {t.content}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => preview(t)} className="text-xs"><Eye className="w-3 h-3 mr-1" /> Vista previa</Button>
              <Button variant="ghost" size="sm" onClick={() => { setEditData({ ...t }); setEditOpen(true); }} className="text-xs"><Pencil className="w-3 h-3 mr-1" /> Editar</Button>
              <Button variant="ghost" size="sm" onClick={() => toggle(t.id)} className="text-xs">
                {t.is_active ? <><UserX className="w-3 h-3 mr-1" /> Desactivar</> : <><CheckCircle className="w-3 h-3 mr-1" /> Activar</>}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[85vh] overflow-y-auto" data-testid="wa-template-modal">
          <DialogHeader>
            <DialogTitle className="heading-industrial uppercase">{editData?.id ? "Editar Plantilla" : "Nueva Plantilla"}</DialogTitle>
          </DialogHeader>
          {editData && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Nombre *</label>
                  <Input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} data-testid="wa-tpl-name" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Categoria</label>
                  <Input value={editData.category} onChange={e => setEditData({ ...editData, category: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-medium">Descripcion</label>
                <Input value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-500 font-medium">Contenido del mensaje *</label>
                <textarea value={editData.content} onChange={e => setEditData({ ...editData, content: e.target.value })} className="w-full border border-slate-200 rounded-sm p-2 text-sm h-32 resize-none font-mono" data-testid="wa-tpl-content" />
                <p className="text-[10px] text-slate-400 mt-1">Variables: {"{cliente}"}, {"{asesor}"}, {"{vehiculo}"}, {"{matricula}"}, {"{empresa}"}, {"{horario_asesor}"}, {"{telefono_asesor}"}, {"{numero_expediente}"}, {"{numero_orden}"}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Orden</label>
                  <Input type="number" value={editData.sort_order} onChange={e => setEditData({ ...editData, sort_order: parseInt(e.target.value) || 0 })} className="rounded-sm" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editData.is_favorite} onChange={e => setEditData({ ...editData, is_favorite: e.target.checked })} />
                    Favorita
                  </label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="wa-save-template-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="wa-template-preview">
          <DialogHeader>
            <DialogTitle className="heading-industrial uppercase">Vista previa: {previewTpl?.name}</DialogTitle>
          </DialogHeader>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{previewRendered}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => copyToClipboard(previewRendered)} className="rounded-sm flex-1 text-xs">
              <Copy className="w-3 h-3 mr-1" /> Copiar mensaje
            </Button>
            <Button onClick={() => { window.open(`https://web.whatsapp.com/send?phone=34611111111&text=${encodeURIComponent(previewRendered)}`, '_blank'); }} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm flex-1 text-xs" data-testid="wa-preview-open-wa">
              <ExternalLink className="w-3 h-3 mr-1" /> Abrir WhatsApp
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════
// COMMUNICATIONS TAB
// ═══════════════════════════════════════════

function CommunicationsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOrigin, setFilterOrigin] = useState("");
  const [filterAction, setFilterAction] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filterOrigin) params.append("origin", filterOrigin);
    if (filterAction) params.append("action_type", filterAction);
    params.append("limit", "100");
    api.get(`/customer-communications?${params}`).then(r => setItems(r.data)).catch(() => toast.error("Error")).finally(() => setLoading(false));
  }, [api, filterOrigin, filterAction]);

  useEffect(() => { load(); }, [load]);

  const ACTION_LABELS = {
    open_whatsapp: { label: "WhatsApp abierto", cls: "bg-emerald-100 text-emerald-700" },
    copy_message: { label: "Mensaje copiado", cls: "bg-sky-100 text-sky-700" },
    manual_note: { label: "Nota manual", cls: "bg-slate-100 text-slate-600" },
    public_lookup: { label: "Busqueda publica", cls: "bg-amber-100 text-amber-700" },
    attention_request: { label: "Solicitud atencion", cls: "bg-red-100 text-red-700" },
  };

  const ORIGIN_LABELS = {
    internal_panel: { label: "Panel interno", cls: "bg-indigo-100 text-indigo-700" },
    public_portal: { label: "Portal publico", cls: "bg-amber-100 text-amber-700" },
  };

  if (loading) return <div className="text-center py-12 text-sm text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-4" data-testid="wa-communications-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Registro de comunicaciones ({items.length})</h3>
        <div className="flex items-center gap-2 ml-auto">
          <Select value={filterOrigin || "__all__"} onValueChange={v => setFilterOrigin(v === "__all__" ? "" : v)}>
            <SelectTrigger className="rounded-sm w-40 h-8 text-xs"><SelectValue placeholder="Origen" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todo origen</SelectItem>
              <SelectItem value="internal_panel">Panel interno</SelectItem>
              <SelectItem value="public_portal">Portal publico</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterAction || "__all__"} onValueChange={v => setFilterAction(v === "__all__" ? "" : v)}>
            <SelectTrigger className="rounded-sm w-44 h-8 text-xs"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todo tipo</SelectItem>
              <SelectItem value="open_whatsapp">WhatsApp abierto</SelectItem>
              <SelectItem value="copy_message">Mensaje copiado</SelectItem>
              <SelectItem value="manual_note">Nota manual</SelectItem>
              <SelectItem value="public_lookup">Busqueda publica</SelectItem>
              <SelectItem value="attention_request">Solicitud atencion</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="divide-y divide-slate-100">
          {items.map(c => {
            const action = ACTION_LABELS[c.action_type] || { label: c.action_type, cls: "bg-slate-100" };
            const origin = ORIGIN_LABELS[c.origin] || { label: c.origin, cls: "bg-slate-100" };
            return (
              <div key={c.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`wa-comm-${c.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge className={`text-[9px] ${action.cls}`}>{action.label}</Badge>
                      <Badge className={`text-[9px] ${origin.cls}`}>{origin.label}</Badge>
                      {c.template_name && <Badge className="bg-slate-100 text-slate-500 text-[9px]">{c.template_name}</Badge>}
                    </div>
                    <p className="text-sm text-slate-900">
                      {c.client_name && <span className="font-medium">{c.client_name}</span>}
                      {c.client_phone && <span className="text-slate-500 ml-2 font-mono text-xs">{c.client_phone}</span>}
                      {c.vehicle_plate && <span className="text-slate-500 ml-2 font-mono text-xs font-bold">{c.vehicle_plate}</span>}
                    </p>
                    {c.advisor_name && <p className="text-xs text-slate-500 mt-0.5">Asesor: {c.advisor_name}</p>}
                    {c.message_text && <p className="text-xs text-slate-600 mt-1 bg-slate-50 rounded-sm p-2 whitespace-pre-wrap">{c.message_text}</p>}
                    {c.notes && <p className="text-xs text-slate-400 mt-1 italic">{c.notes}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-slate-400">{formatDate(c.created_at)}</p>
                    <p className="text-[10px] text-slate-400">{c.created_by}</p>
                  </div>
                </div>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-400">Sin comunicaciones registradas</div>
          )}
        </div>
      </div>
    </div>
  );
}
